import React, { useContext, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ConfigContext } from '../../contexts/config-context';
import { InterfaceContext } from '../../contexts/interface-context';
import { UserContext } from '../../contexts/userContext';
import { useCounts } from '../../contexts/counts-context';
import { useLogout } from '../logout/logout';
import { BrandLogo } from '../shared-components/site-header/brand-logo';
import { PhoneIcon } from '../shared-components/listing-icons';
import { PanelIcon, PANEL_MENU, shownCount } from '../../pages/user-panel/user-panel';
import '../../hs-theme.css';
import './responsive-navigation.css';

const LINKS = [
    { title: 'home', path: '/' },
    { title: 'buy', path: '/prodaja' },
    { title: 'rent', path: '/izdavanje' },
    { title: 'daily', path: '/stan-na-dan' },
    { title: 'new_build', path: '/novogradnja' },
    { title: 'info', path: '/uputstvo' },
];
const PHONE = '062 048 423';
// a signed in user gets the panel pages, in these groups; the site pages are only listed for guests
const PANEL_GROUPS = [
    ['/korisnik/oglasi', '/korisnik/neaktivni-oglasi', '/korisnik/poruke', '/korisnik/obavestenja', '/korisnik/praceni'],
    ['/korisnik/kredit', '/korisnik/oglasavanje'],
    ['/korisnik/podaci', '/korisnik/sigurnost', '/korisnik/kontakt'],
].map(group => group.map(path => PANEL_MENU.find(item => item.path === path)));

const ResponsiveNavigation = () => {
    const { closeResponsiveMenu } = useContext(InterfaceContext);
    const { api } = useContext(ConfigContext);
    const { user } = useContext(UserContext);
    const { i18n, t } = useTranslation(["navigation", "profile"]);
    const location = useLocation();
    const logout = useLogout();
    const counts = useCounts();

    const lang = i18n.language === 'en' ? 'en' : 'sr';

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') closeResponsiveMenu(); };
        document.addEventListener('keydown', onKey);
        // the page behind the menu should not scroll
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflow;
        };
    }, []);

    const follow = () => {
        sessionStorage.removeItem('currentPage');
        closeResponsiveMenu();
    };

    return (
        <div className='hs hs-drawer-overlay' onClick={closeResponsiveMenu}>
            <aside className='hs-drawer' role='dialog' aria-label='Menu' onClick={(e) => e.stopPropagation()}>
                <div className='hs-drawer-head'>
                    <Link to='/' onClick={follow}><BrandLogo /></Link>
                    <button className='hs-drawer-close' onClick={closeResponsiveMenu} aria-label='Close'>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
                    </button>
                </div>

                <div className='hs-drawer-body'>
                    {user ? <>
                        <div className='hs-drawer-user'>
                            <span className='hs-drawer-avatar' style={user.photo ? { backgroundImage: `url(${api.host}:${api.port}${user.photo})` } : undefined}>
                                {!user.photo && `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()}
                            </span>
                            <span className='hs-drawer-user-text'>
                                <span className='hs-drawer-user-name'>{user.first_name} {user.last_name}</span>
                                <span className='hs-drawer-user-mail'>{user.email}</span>
                            </span>
                        </div>

                        {PANEL_GROUPS.map((group, i) => (
                            <div key={`group-${i}`} className='hs-drawer-group'>
                                {group.map(item => {
                                    const count = shownCount(item, counts);
                                    return (
                                        <Link key={item.path} to={item.path} onClick={follow}
                                            className={`hs-drawer-item ${location.pathname === item.path ? 'on' : ''}`}>
                                            <span className='hs-drawer-item-icon'><PanelIcon name={item.icon} size={16} /></span>
                                            <span className='hs-drawer-item-label'>{t(item.title, { ns: 'profile' })}</span>
                                            {count !== undefined &&
                                                <span className={`hs-drawer-count ${item.alert ? 'alert' : ''}`}>{count}</span>}
                                        </Link>
                                    );
                                })}
                            </div>
                        ))}
                    </> : LINKS.map(item => (
                        <Link key={item.path} to={item.path} onClick={follow}
                            className={`hs-drawer-link ${location.pathname === item.path ? 'on' : ''}`}>
                            {t(item.title)}
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9AA5B4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                        </Link>
                    ))}
                </div>

                <div className='hs-drawer-foot'>
                    {/* posting has its own button in the tab bar or the header, so it is not repeated here */}
                    {user
                        ? <button className='hs-drawer-outline logout' onClick={(e) => { closeResponsiveMenu(); logout(e); }}>{t('logout', { ns: 'profile' })}</button>
                        : <Link to='/login' onClick={follow} className='hs-drawer-outline'>{t('login')}</Link>}
                    <div className='hs-drawer-foot-row'>
                        <div className='hs-lang'>
                            <button className={lang === 'sr' ? 'on' : ''} onClick={() => i18n.changeLanguage('sr')}>SR</button>
                            <button className={lang === 'en' ? 'on' : ''} onClick={() => i18n.changeLanguage('en')}>EN</button>
                        </div>
                        <a href={`tel:${PHONE.replace(/\s/g, '')}`} className='hs-drawer-phone'>
                            <span className='hs-drawer-phone-icon'><PhoneIcon size={15} /></span>
                            {PHONE}
                        </a>
                    </div>
                </div>
            </aside>
        </div>
    );
};

export default memo(ResponsiveNavigation);
